<script setup lang="ts">
import { HousingFinderInput } from "~/api";
import { formatNumber } from "~/utils/miscUtils";
import { useGeoTree, buildTreeNodeMap } from "~/composables/useGeoTree";
import { useUnitTypeTree } from "~/composables/useUnitTypeTree";
import { useSectorTree } from "~/composables/useSectorTree";
import { useHousingOrgStore } from "~/YYC_Housing/stores/HousingOrgStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import type { TreeNodeDto } from "~/api";

import { computed, onMounted, ref } from "vue";

const props = defineProps<{
  finderInputs: HousingFinderInput;
}>();

const housingOrgStore = useHousingOrgStore();
const myInfoStore = useMyInfoStore();
const { loadGeoTree, getNodeName } = useGeoTree();
const { loadUnitTypeTree, getNodeName: getUnitTypeNodeName } =
  useUnitTypeTree();
const { loadSectorTree, getNodeName: getSectorNodeName } = useSectorTree();

const buildingTypeMap = ref(new Map<string, TreeNodeDto>());
const buildingAttrMap = ref(new Map<string, TreeNodeDto>());

const loadBuildingTrees = async () => {
  await myInfoStore.load();
  const org = myInfoStore.currentSysOrgGuid;
  const [bt, ba] = await Promise.all([
    apiClient.tree
      .getTreeDefinitionByType(org, "BuildingType")
      .catch(() => null),
    apiClient.tree
      .getTreeDefinitionByType(org, "BuildingAttribute")
      .catch(() => null),
  ]);
  buildingTypeMap.value.clear();
  buildTreeNodeMap(bt?.nodes ?? [], buildingTypeMap.value);
  buildingAttrMap.value.clear();
  buildTreeNodeMap(ba?.nodes ?? [], buildingAttrMap.value);
};

const isGuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const locationDisplay = computed(() => {
  return props.finderInputs?.locations
    ?.map((loc) => {
      if (isGuid(loc)) {
        return getNodeName(loc) ?? null;
      }
      return loc;
    })
    .filter((loc): loc is string => loc !== null);
});

const sectorDisplay = computed(() => {
  return props.finderInputs?.sectorNodeGuids
    ?.map((guid) => getSectorNodeName(guid) ?? guid)
    .filter(Boolean);
});

const buildingTypesDisplay = computed(() => {
  return props.finderInputs?.buildingTypes
    ?.map((guid) => buildingTypeMap.value.get(guid)?.name ?? guid)
    .filter(Boolean);
});

const buildingAttributesDisplay = computed(() => {
  return props.finderInputs?.buildingAttributes
    ?.map((guid) => buildingAttrMap.value.get(guid)?.name ?? guid)
    .filter(Boolean);
});

const legacyRoomLabel = (code: string): string => {
  if (code === "s") return "Studio";
  if (code === "1") return "1 Bedroom";
  if (code === "2") return "2 Bedroom";
  if (code === "supports") return "Studio - With Supports";
  return "";
};

const roomOptionsSelected = computed(() => {
  const v2 = props.finderInputs?.roomPreferenceV2 ?? [];
  if (v2.length > 0) {
    return v2.map((guid) => getUnitTypeNodeName(guid) ?? "");
  }
  return props.finderInputs?.roomPreference?.map(legacyRoomLabel);
});

const housingOrgsSelected = computed(() => {
  return props.finderInputs?.housingOrgs?.map((org) => {
    return housingOrgStore.getNameByCode(org);
  });
});

onMounted(async () => {
  await Promise.all([
    housingOrgStore.load(),
    loadGeoTree(),
    loadUnitTypeTree(),
    loadSectorTree(),
    loadBuildingTrees(),
  ]);
});
</script>
<template>
  <div>
    <div class="filter-group">
      <div class="filter-heading">Inputs</div>
      <div class="a1 input">Age: {{ finderInputs.age1 }}</div>
      <div v-if="finderInputs.age2" class="a2 input">
        Co-applicant Age: {{ finderInputs.age2 }}
      </div>
      <div class="i1 input">
        Income: ${{ formatNumber(finderInputs.income1, 0) }}
      </div>
      <div v-if="finderInputs.income2" class="i2 input">
        Co-applicant Income: ${{ formatNumber(finderInputs.income2, 0) }}
      </div>
    </div>

    <div v-if="sectorDisplay?.length" class="filter-group">
      <div class="filter-heading">Sectors</div>
      <ul>
        <li v-for="sector in sectorDisplay" :key="sector">
          {{ sector }}
        </li>
      </ul>
    </div>

    <div
      v-if="roomOptionsSelected?.length || finderInputs.accessible"
      class="filter-group"
    >
      <div class="filter-heading">Room Options</div>
      <ul>
        <li v-for="room in roomOptionsSelected" :key="room">
          {{ room }}
        </li>
      </ul>
      <el-tag
        v-if="finderInputs.accessible"
        size="small"
        type="warning"
        style="align-self: start"
      >
        Accessible Unit
      </el-tag>
    </div>

    <div v-if="buildingTypesDisplay?.length" class="filter-group">
      <div class="filter-heading">Building Types</div>
      <ul>
        <li v-for="bt in buildingTypesDisplay" :key="bt">
          {{ bt }}
        </li>
      </ul>
    </div>

    <div v-if="buildingAttributesDisplay?.length" class="filter-group">
      <div class="filter-heading">Attributes</div>
      <ul>
        <li v-for="attr in buildingAttributesDisplay" :key="attr">
          {{ attr }}
        </li>
      </ul>
    </div>

    <div v-if="housingOrgsSelected?.length" class="filter-group">
      <div class="filter-heading">Housing Organizations</div>
      <ul>
        <li v-for="org in housingOrgsSelected" :key="org">
          {{ org }}
        </li>
      </ul>
    </div>

    <div v-if="locationDisplay?.length" class="filter-group">
      <div class="filter-heading">Locations</div>
      <ul>
        <li v-for="loc in locationDisplay" :key="loc">
          {{ loc }}
        </li>
      </ul>
    </div>
  </div>
</template>
<style lang="less" scoped>
.input {
  width: max-content;
}
.filter-group {
  margin-bottom: 0.75rem;
  .filter-heading {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--el-text-color-secondary);
    margin-bottom: 0.15em;
  }
  ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
  }
}
.filter-tag {
  font-size: 0.85em;
  color: var(--el-color-warning);
  font-weight: 500;
  margin-top: 0.25em;
}
</style>
